import {
    type ChangeEvent,
	type HTMLAttributes,
	type KeyboardEvent,
	type MouseEvent,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react"
import { useOnClickOutside } from "../../hooks/useOnClickOutside"
import { useControlled } from "../../hooks/useControlled"

import { Box, Tooltip } from "@mui/material"

interface EditableTextProps extends HTMLAttributes<HTMLDivElement> {
	value: string
}

function EditableText({ value: valueProps, ...props}: EditableTextProps) {
	const { value, setValue } = useControlled(valueProps, '')
	const [isEditing, setIsEditing] = useState(false)

	const inputRef = useRef<HTMLInputElement | null>(null)

	const resizeInput = (input: HTMLInputElement) => {
		const span = document.createElement('span');
		span.style.visibility = 'hidden';
		span.style.position = 'absolute';
		span.style.fontSize = getComputedStyle(input).fontSize;
		span.style.fontFamily = getComputedStyle(input).fontFamily;
		span.style.fontWeight = getComputedStyle(input).fontWeight;
		span.textContent = input.value || input.placeholder || '';
		
		document.body.appendChild(span);
		const width = span.offsetWidth + 8;
		document.body.removeChild(span);
		
		input.style.width = Math.max(width, 16) + 'px';
	}

	const handleValueChange = useCallback((evt: ChangeEvent<HTMLInputElement>) => {
		const newValue = evt.target.value

		setValue(newValue)
	}, [setValue])

	const handleEditingDisable = () => {
		setIsEditing(false)
	}

	const handleEditingToggle = (evt: MouseEvent<HTMLDivElement>) => {
		setIsEditing((prev) => !prev)
	}

	const handleShortcut = (evt: KeyboardEvent<HTMLInputElement>) => {
		if (['Enter', 'Escape'].includes(evt.key)) {
			handleEditingDisable()
		}
	}

	useEffect(() => {
		const input = inputRef.current

		if (input) {
			input.focus()
		}
	}, [isEditing])

	useEffect(() => {
		const input = inputRef.current

		if (input) {
			resizeInput(input)
		}
	}, [value])

	useOnClickOutside(inputRef, handleEditingDisable)

	const height = props.style?.height || '2rem'

	return (
		<Box
			onDoubleClick={handleEditingToggle}
			sx={{
				position: 'relative',
				isolation: 'isolate',
				minWidth: '1rem',
				maxWidth: '4rem',
				height,
			}}
			{...props}
		>
			<input
				ref={inputRef}
				value={value}
				onChange={handleValueChange}
				onKeyUp={handleShortcut}
				style={{
					all: 'unset',
					width: 'min-content',
					maxWidth: 'inherit',
					minWidth: 'inherit',
					height: '100%',
					fontSize: 'inherit',

					display: isEditing ? 'inline-flex' : 'none'
				}}
			/>

			<Tooltip title={value}>
				<p 
					style={{
						display: isEditing ? 'none': 'inline-block',
						alignItems: 'center',
						maxWidth: 'inherit',
						minWidth: 'inherit',

						height: '100%',
						lineHeight: height,

						margin: 0,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',

						fontSize: 'inherit'
					}}
				>
					{value}
				</p>
			</Tooltip>
		</Box>
	)
}

export { EditableText, type EditableTextProps }